import React, { useEffect, useState } from 'react';
import { ShieldCheck, ShieldOff, AlertTriangle } from 'lucide-react';
import { organizationsService } from 'api/services/organizations.service';

interface Props {
  organizationId: string;
  organizationName?: string;
  /** True when the viewer belongs to this business — only they may change it. */
  canChange: boolean;
  onChanged?: (allowed: boolean) => void;
}

/**
 * A business decides whether its service provider may reach in.
 *
 * <p>Shown to the BUSINESS, and read-only to anyone else. A provider able to
 * re-enable its own access would make this decoration — consent restored by the party
 * it was withdrawn from — so the server refuses that and this reflects it rather than
 * offering a control that would fail.
 *
 * <p>This card is the first thing in what is meant to become a business-managed
 * section: the place a business controls what its provider may do, and later where it
 * can ask the provider for things.
 */
export const ProviderAccessCard: React.FC<Props> = ({
  organizationId, organizationName, canChange, onChanged,
}) => {
  const [value, setValue] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  // Read the CURRENT state rather than trusting the login payload.
  //
  // The session's organisation list is captured at sign-in and never carried this
  // field at all, so it read as "allowed" always — a business that had withdrawn
  // access would still be shown as granting it, and would toggle from the wrong
  // starting point. Even once the field is included, a change made after sign-in
  // would not appear until the next login.
  useEffect(() => {
    let alive = true;
    (async () => {
      const r = await organizationsService.getOrganizationById(organizationId);
      if (!alive) return;
      // Absent means allowed: the column went onto a live table with no backfill,
      // so "not set" must never render as "blocked".
      setValue((r.data as any)?.allowProviderAccess !== false);
    })();
    return () => { alive = false; };
  }, [organizationId]);

  const toggle = async (next: boolean) => {
    // Withdrawing is the direction worth pausing on. Granting back is not — nobody
    // needs protecting from restoring access they chose to remove.
    if (!next && !window.confirm(
      `Stop your service provider from accessing ${organizationName || 'this business'}?\n\n` +
      `They will no longer be able to see or change anything here — including your ` +
      `agents, prompts, contacts and campaigns. You can turn this back on at any time.`)) return;

    setSaving(true);
    setNote(null);
    const r = await organizationsService.setProviderAccess(organizationId, next);
    setSaving(false);
    if (!r.success) { setNote(r.error || 'Could not change provider access'); return; }
    setValue(next);
    setNote(r.message || null);
    onChanged?.(next);
  };

  // Nothing is claimed until the real state is known. Rendering "Allowed" while the
  // answer is still in flight is the same lie this card exists to prevent.
  if (value === null) return null;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 space-y-3">
      <div className="flex items-start gap-3">
        {value
          ? <ShieldCheck className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
          : <ShieldOff className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />}
        <div className="min-w-0 flex-1">
          <h3 className="font-medium text-gray-900 dark:text-white">Service provider access</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {value
              ? 'Your service provider can see and manage this business — agents, prompts, contacts and campaigns.'
              : 'Your service provider cannot access this business. They can still see that it exists.'}
          </p>
        </div>
        <label className="inline-flex items-center gap-2 shrink-0">
          <input
            type="checkbox"
            checked={value}
            disabled={!canChange || saving}
            onChange={(e) => toggle(e.target.checked)}
          />
          <span className="text-sm text-gray-600 dark:text-gray-300">
            {saving ? 'Saving…' : value ? 'Allowed' : 'Blocked'}
          </span>
        </label>
      </div>

      {!canChange && (
        // Explains WHY it is greyed out. A disabled control with no reason reads as
        // a broken screen.
        <p className="flex items-start gap-2 text-xs text-gray-500 dark:text-gray-400">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          Only this business can change this. A provider cannot restore access a
          business has withdrawn.
        </p>
      )}

      {note && <p className="text-sm text-gray-700 dark:text-gray-200">{note}</p>}
    </div>
  );
};
